// components/review/use-review-item-actions.ts
import { useState } from "react"
import type { QueueItem } from "@/lib/review/queue"
import {
  dispatchCart,
  getCart,
  type CartItem,
} from "@/components/editor/edit-cart"
import { checkValue, hasBlockingErrors } from "@/lib/review/value-checks"
import type { ValueIssue } from "@/lib/review/value-checks"
import type { Glossary } from "@/lib/review/glossary"
import { isLargeString } from "./locate-state"
import { enqueue } from "./pending-commits"
import { getReviewState } from "./review-store"

/**
 * Pure value-QA for the cockpit: run the same checks the server gate runs
 * against the value the reviewer is about to approve. Errors (placeholder / tag)
 * block approval; glossary issues are warnings the panel renders amber.
 */
export function computeIssues(
  item: QueueItem,
  value: string,
  glossary: Glossary
): ValueIssue[] {
  return checkValue({
    key: item.key,
    locale: item.locale,
    enValue: item.enValue,
    targetValue: value,
    glossary,
  })
}

/** Status pill copy. "modified" = the approved translation was edited since
 *  sign-off (soft re-queue), so it's back for re-approval. */
export function statusLabel(status: QueueItem["status"]): string {
  return status === "modified" ? "edited since approval" : status
}

/**
 * Shared approve/edit/skip sign-off logic extracted verbatim from ReviewPanel so
 * the desktop panel and MobileReviewView drive the exact same cart + pending-commit
 * path. `value`/`busy`/`edited`/`large` plus `approveAsIs`/`editAndApprove` keep
 * their original behavior; presentation (refs, full-editor toggle) stays in the
 * components.
 */
export function useReviewItemActions({
  item,
  onDone,
  idToken,
  glossary,
}: {
  item: QueueItem
  onDone: () => void
  idToken: string
  glossary: Glossary
}) {
  const [value, setValue] = useState(item.targetValue)
  const [busy, setBusy] = useState(false)
  const edited = value.trim() !== item.targetValue.trim()
  const large = isLargeString(item.enValue)
  // Re-run the value gate on every render so the panel can surface issues live
  // and block approval of a value with a placeholder/tag error.
  const issues = computeIssues(item, value, glossary)
  const blocked = hasBlockingErrors(issues)

  function approveAsIs() {
    if (hasBlockingErrors(computeIssues(item, item.targetValue, glossary)))
      return
    setBusy(true)
    const index = getReviewState().cursor
    onDone()
    enqueue({
      kind: "approve",
      key: item.key,
      commit: {
        item,
        approvedValue: item.targetValue,
        pendingMerge: false,
        idToken,
      },
      revert: { item, index, priorCartItem: null },
    })
  }
  function editAndApprove() {
    if (blocked) return
    setBusy(true)
    const index = getReviewState().cursor
    const priorCartItem: CartItem | null =
      getCart().find((i) => i.key === item.key) ?? null
    dispatchCart({
      type: "add",
      item: {
        key: item.key,
        source: "intl",
        page: item.jumpPath || "/",
        values: { [item.locale]: { old: item.targetValue, new: value } },
      },
    })
    onDone()
    enqueue({
      kind: "edit",
      key: item.key,
      commit: { item, approvedValue: value, pendingMerge: true, idToken },
      revert: { item, index, priorCartItem },
    })
  }

  return {
    value,
    setValue,
    busy,
    edited,
    large,
    issues,
    blocked,
    approveAsIs,
    editAndApprove,
  }
}
